import { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import axios from "axios";
import { Loader2, RefreshCw, CheckCircle2, ChevronDown, ChevronRight, Wallet, TrendingUp, IndianRupee, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  } catch {
    return iso;
  }
};

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n || 0);

const nowMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const monthLabel = (m) => {
  try {
    const [y, mo] = m.split("-");
    const d = new Date(Number(y), Number(mo) - 1, 1);
    return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  } catch {
    return m;
  }
};

const monthOptions = () => {
  const list = [];
  const d = new Date();
  d.setDate(1);
  for (let i = 0; i < 12; i++) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    list.push(`${y}-${m}`);
    d.setMonth(d.getMonth() - 1);
  }
  return list;
};

export default function AdminPayouts() {
  const { authHeader, apiBase } = useAuth();
  const [month, setMonth] = useState(nowMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${apiBase}/admin/payouts`, {
        params: { month },
        headers: authHeader(),
      });
      setData(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load payouts");
    } finally {
      setLoading(false);
    }
  }, [apiBase, authHeader, month]);

  useEffect(() => {
    load();
  }, [load]);

  const markPaid = async (vendorId) => {
    setBusyId(vendorId);
    try {
      await axios.post(
        `${apiBase}/admin/payouts/${vendorId}/mark-paid`,
        { month },
        { headers: authHeader() },
      );
      toast.success("Marked as paid");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Mark-paid failed");
    } finally {
      setBusyId(null);
    }
  };

  const unmarkPaid = async (vendorId) => {
    if (!window.confirm("Undo the paid status for this vendor?")) return;
    setBusyId(vendorId);
    try {
      await axios.delete(`${apiBase}/admin/payouts/${vendorId}/mark-paid`, {
        params: { month },
        headers: authHeader(),
      });
      toast.success("Reverted to pending");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Undo failed");
    } finally {
      setBusyId(null);
    }
  };

  const downloadStatement = async (row) => {
    try {
      const resp = await axios.get(
        `${apiBase}/admin/payouts/${row.vendor_id}/statement`,
        {
          params: { month },
          headers: authHeader(),
          responseType: "blob",
        },
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sriman-payout-${month}-${(row.vendor_name || "partner")
        .toLowerCase()
        .replace(/\s+/g, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Failed to generate statement");
    }
  };

  const totals = data?.totals || {
    vendors: 0,
    won_count: 0,
    gross_amount: 0,
    paid_amount: 0,
    outstanding_amount: 0,
  };
  const rowsWithEarnings = useMemo(
    () => (data?.rows || []),
    [data],
  );
  const rowsZero = [];

  return (
    <div data-testid="admin-payouts-panel">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <span className="overline">/Admin · Payouts</span>
          <h1 className="mt-4 font-display text-white text-4xl md:text-5xl tracking-tighter font-light leading-tight">
            Monthly payout ledger
          </h1>
          <p className="mt-3 text-slate-500 text-sm">
            Every partner earns {formatINR(20000)} + GST per successful sale (won lead). Released on
            or before the 5th of every calendar month.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            data-testid="payouts-month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="bg-[#0B132B]/60 border border-white/10 focus:border-red-500/60 text-white text-sm px-4 py-2.5 rounded-sm focus:outline-none"
          >
            {monthOptions().map((m) => (
              <option key={m} value={m}>
                {monthLabel(m)}
              </option>
            ))}
          </select>
          <button
            onClick={load}
            disabled={loading}
            data-testid="payouts-refresh"
            className="inline-flex items-center gap-2 border border-white/15 hover:border-white text-white text-sm px-4 py-2.5 rounded-sm transition-colors disabled:opacity-60"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-[#0B132B]/50 border border-white/10 p-5">
          <div className="flex items-center gap-2 text-slate-500">
            <TrendingUp size={13} />
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase">Sales</div>
          </div>
          <div className="mt-2 font-display text-white text-3xl">{totals.won_count}</div>
        </div>
        <div className="bg-[#0B132B]/50 border border-white/10 p-5">
          <div className="flex items-center gap-2 text-slate-500">
            <IndianRupee size={13} />
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase">Gross accrued</div>
          </div>
          <div className="mt-2 font-display text-white text-3xl">{formatINR(totals.gross_amount)}</div>
        </div>
        <div className="bg-[#0B132B]/50 border border-white/10 p-5">
          <div className="flex items-center gap-2 text-slate-500">
            <CheckCircle2 size={13} />
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase">Paid out</div>
          </div>
          <div className="mt-2 font-display text-green-300 text-3xl">
            {formatINR(totals.paid_amount)}
          </div>
        </div>
        <div className="bg-[#0B132B]/50 border border-red-500/40 p-5">
          <div className="flex items-center gap-2 text-red-400">
            <Wallet size={13} />
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase">Outstanding</div>
          </div>
          <div className="mt-2 font-display text-white text-3xl">
            {formatINR(totals.outstanding_amount)}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="mt-8 bg-[#0B132B]/40 border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left">
                {["", "Vendor", "Sales", "Amount", "Status", "Action"].map((h, i) => (
                  <th
                    key={i}
                    className="px-4 py-4 font-mono text-[10px] text-slate-500 tracking-[0.22em] uppercase"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="px-6 py-16 text-center">
                    <Loader2 size={20} className="inline animate-spin text-slate-500" />
                  </td>
                </tr>
              )}
              {!loading && rowsWithEarnings.length === 0 && rowsZero.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-16 text-center text-slate-500 text-sm">
                    No vendors yet.
                  </td>
                </tr>
              )}
              {!loading && rowsWithEarnings.length === 0 && rowsZero.length > 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-slate-500 text-sm">
                    No won deals for {monthLabel(month)} yet.
                  </td>
                </tr>
              )}
              {!loading &&
                rowsWithEarnings.map((r) => {
                  const open = !!expanded[r.vendor_id];
                  return (
                    <Fragment key={r.vendor_id}>
                      <tr
                        data-testid={`payout-row-${r.vendor_id}`}
                        className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors"
                      >
                        <td className="px-4 py-4 w-8">
                          <button
                            onClick={() =>
                              setExpanded((s) => ({ ...s, [r.vendor_id]: !s[r.vendor_id] }))
                            }
                            className="text-slate-400 hover:text-white"
                            data-testid={`payout-expand-${r.vendor_id}`}
                          >
                            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </button>
                        </td>
                        <td className="px-4 py-4 text-white">
                          <div>{r.vendor_name}</div>
                          <div className="text-slate-500 text-xs">{r.vendor_email}</div>
                        </td>
                        <td className="px-4 py-4 text-white font-mono">{r.won_count}</td>
                        <td className="px-4 py-4 text-white font-mono">{formatINR(r.amount)}</td>
                        <td className="px-4 py-4">
                          {r.status === "paid" ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border font-mono text-[10px] tracking-[0.14em] uppercase bg-green-500/15 text-green-300 border-green-500/40">
                              <CheckCircle2 size={11} />
                              Paid · {formatDate(r.paid_at)}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border font-mono text-[10px] tracking-[0.14em] uppercase bg-yellow-500/15 text-yellow-300 border-yellow-500/40">
                              Pending
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() => downloadStatement(r)}
                              data-testid={`payout-statement-${r.vendor_id}`}
                              className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white text-white text-xs px-2.5 py-1.5 rounded-sm transition-colors"
                              title="Download PDF statement"
                            >
                              <Download size={12} />
                              PDF
                            </button>
                            {r.status === "paid" ? (
                              <button
                                onClick={() => unmarkPaid(r.vendor_id)}
                                disabled={busyId === r.vendor_id}
                                data-testid={`payout-unmark-${r.vendor_id}`}
                                className="text-slate-500 hover:text-red-400 text-xs disabled:opacity-60"
                              >
                                Undo
                              </button>
                            ) : r.amount > 0 ? (
                              <button
                                onClick={() => markPaid(r.vendor_id)}
                                disabled={busyId === r.vendor_id}
                                data-testid={`payout-mark-paid-${r.vendor_id}`}
                                className="inline-flex items-center gap-1.5 bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white text-xs font-medium px-3 py-1.5 rounded-sm"
                              >
                                {busyId === r.vendor_id ? (
                                  <Loader2 size={12} className="animate-spin" />
                                ) : (
                                  <CheckCircle2 size={12} />
                                )}
                                Mark paid
                              </button>
                            ) : (
                              <span className="text-slate-600 text-xs font-mono">No sales</span>
                            )}
                          </div>
                        </td>
                      </tr>
                      {open && (
                        <tr className="bg-[#040812]/60">
                          <td colSpan={6} className="px-6 py-4">
                            <div className="overline mb-2">Won leads this month</div>
                            <ul className="space-y-1">
                              {r.wins.map((w) => (
                                <li
                                  key={w.lead_id}
                                  className="flex items-center gap-3 text-sm text-slate-300"
                                >
                                  <span className="font-mono text-[10px] text-slate-500 tracking-widest uppercase">
                                    {formatDate(w.won_at)}
                                  </span>
                                  <span className="text-white">{w.name}</span>
                                  {w.company && (
                                    <span className="text-slate-500">· {w.company}</span>
                                  )}
                                  {w.product_name && (
                                    <span className="ml-auto font-mono text-[10px] tracking-widest uppercase text-red-400">
                                      {w.product_name}
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {rowsZero.length > 0 && (
        <div className="mt-6 text-slate-500 text-xs font-mono tracking-widest uppercase">
          {rowsZero.length} vendor(s) with no wins this month — hidden.
        </div>
      )}
    </div>
  );
}
